import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  CheckCircle,
  XCircle,
  Confetti,
  ArrowLeft,
  Rocket,
  Gear,
  StripeLogo,
  GoogleLogo,
  Calendar,
  Package,
  FileText,
} from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { useAppNavigate } from '@/hooks/useAppNavigate'

type ReviewStepProps = {
  onBack: () => void
  onComplete: () => Promise<void>
  status: {
    stripe: boolean
    calendar: boolean
  } | null
  availability: {
    configured: boolean
    timeZone: string | null
    activeWindowsCount: number
  } | null
  hasPackages: boolean
  hasContractTemplate: boolean
}

type ChecklistItem = {
  id: string
  label: string
  completed: boolean
  icon: typeof StripeLogo
  required: boolean
  href?: string
}

export function ReviewStep({
  onBack,
  onComplete,
  status,
  availability,
  hasPackages,
  hasContractTemplate,
}: ReviewStepProps) {
  const { t } = useTranslation('onboarding')
  const navigate = useAppNavigate()
  const [completing, setCompleting] = useState(false)

  const checklist: ChecklistItem[] = [
    {
      id: 'stripe',
      label: t('review.checklist.stripe'),
      completed: status?.stripe ?? false,
      icon: StripeLogo,
      required: true,
      href: '/integrations',
    },
    {
      id: 'google',
      label: t('review.checklist.google'),
      completed: status?.calendar ?? false,
      icon: GoogleLogo,
      required: false,
      href: '/integrations',
    },
    {
      id: 'availability',
      label: t('review.checklist.availability'),
      completed: availability?.configured ?? false,
      icon: Calendar,
      required: true,
      href: '/preferences',
    },
    {
      id: 'packages',
      label: t('review.checklist.packages'),
      completed: hasPackages,
      icon: Package,
      required: false,
      href: '/packages',
    },
    {
      id: 'contracts',
      label: t('review.checklist.contracts'),
      completed: hasContractTemplate,
      icon: FileText,
      required: false,
      href: '/contract-templates',
    },
  ]

  const completedCount = checklist.filter((item) => item.completed).length
  const totalCount = checklist.length
  const requiredComplete = checklist
    .filter((item) => item.required)
    .every((item) => item.completed)
  const allComplete = completedCount === totalCount
  const missingRequiredItems = checklist
    .filter((item) => item.required && !item.completed)
    .map((item) => item.label)

  const handleComplete = async () => {
    try {
      setCompleting(true)
      await onComplete()
      toast.success(t('review.success'))
    } catch (error) {
      console.error('Failed to complete onboarding:', error)
      toast.error(t('review.error'))
    } finally {
      setCompleting(false)
    }
  }

  const handleGoToItem = (href?: string) => {
    if (href) {
      navigate(href.replace(/^\//, ''))
    }
  }

  return (
    <div className="flex flex-col px-4 py-6 animate-in fade-in duration-500">
      {/* Header with celebration */}
      <div className="text-center mb-8">
        <div
          className={cn(
            'inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4',
            allComplete
              ? 'bg-gradient-to-br from-green-400 to-green-500'
              : 'bg-gradient-to-br from-[#F08A24] to-[#f9a84c]'
          )}
        >
          {allComplete ? (
            <Confetti weight="fill" className="w-8 h-8 text-white" />
          ) : (
            <CheckCircle weight="bold" className="w-8 h-8 text-white" />
          )}
        </div>

        <h2 className="text-2xl font-bold text-[#0B2A4A] dark:text-white mb-2">
          {allComplete ? t('review.titleComplete') : t('review.title')}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          {allComplete ? t('review.subtitleComplete') : t('review.subtitle')}
        </p>
      </div>

      {/* Progress summary */}
      <div className="max-w-lg mx-auto w-full mb-6">
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-2">
          <span>{t('review.progress')}</span>
          <span className="font-medium">
            {completedCount}/{totalCount} {t('review.completed')}
          </span>
        </div>
        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-500',
              allComplete
                ? 'bg-gradient-to-r from-green-400 to-green-500'
                : 'bg-gradient-to-r from-[#0B2A4A] to-[#F08A24]'
            )}
            style={{ width: `${(completedCount / totalCount) * 100}%` }}
          />
        </div>
      </div>

      {/* Checklist */}
      <div className="max-w-lg mx-auto w-full space-y-3">
        {checklist.map((item) => (
          <Card
            key={item.id}
            className={cn(
              'transition-all duration-200',
              item.completed
                ? 'border-green-500/30 bg-green-50/30 dark:bg-green-950/10'
                : 'hover:border-gray-300 dark:hover:border-gray-600'
            )}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                {/* Icon */}
                <div
                  className={cn(
                    'flex items-center justify-center w-10 h-10 rounded-lg',
                    item.completed
                      ? 'bg-green-100 dark:bg-green-900/50'
                      : 'bg-gray-100 dark:bg-gray-800'
                  )}
                >
                  <item.icon
                    weight="bold"
                    className={cn(
                      'w-5 h-5',
                      item.completed
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-gray-500 dark:text-gray-400'
                    )}
                  />
                </div>

                {/* Label */}
                <div className="flex-1">
                  <p
                    className={cn(
                      'font-medium',
                      item.completed
                        ? 'text-green-800 dark:text-green-200'
                        : 'text-gray-700 dark:text-gray-300'
                    )}
                  >
                    {item.label}
                  </p>
                  {item.required && !item.completed && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                      {t('review.required')}
                    </p>
                  )}
                </div>

                {/* Status / Action */}
                {item.completed ? (
                  <CheckCircle
                    weight="fill"
                    className="w-6 h-6 text-green-500"
                  />
                ) : (
                  <Button
                    onClick={() => handleGoToItem(item.href)}
                    size="sm"
                    variant="ghost"
                    className="text-[#F08A24] hover:text-[#e07a14] hover:bg-[#F08A24]/10"
                  >
                    {t('review.configure')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Warning for missing required items */}
      {!requiredComplete && (
        <div
          className={cn(
            'flex items-start gap-3 p-4 mt-6 rounded-lg max-w-lg mx-auto w-full',
            'bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/50'
          )}
        >
          <XCircle
            weight="fill"
            className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5"
          />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
              {t('review.missingRequired')}
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
              {t('review.requiredItemsMessage', {
                items: missingRequiredItems.join(', '),
              })}
            </p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col items-center gap-4 mt-10 max-w-lg mx-auto w-full">
        <Button
          onClick={handleComplete}
          disabled={completing || !requiredComplete}
          size="lg"
          className={cn(
            'w-full h-12',
            allComplete
              ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'
              : 'bg-[#F08A24] hover:bg-[#e07a14]',
            'text-white shadow-lg transition-all duration-300 hover:scale-[1.02]'
          )}
        >
          <Rocket weight="fill" className="mr-2 w-5 h-5" />
          {completing ? t('completing') : t('review.startUsing')}
        </Button>

        <div className="flex items-center gap-4">
          <Button
            onClick={onBack}
            variant="ghost"
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
          >
            <ArrowLeft weight="bold" className="mr-2 w-4 h-4" />
            {t('back')}
          </Button>

          <Button
            onClick={() => navigate('preferences')}
            variant="ghost"
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
          >
            <Gear weight="bold" className="mr-2 w-4 h-4" />
            {t('review.reviewSettings')}
          </Button>
        </div>
      </div>
    </div>
  )
}
